/** Шинэ захиалга үүсгэх хуудсууд */
export const ADD_ORDER_TARGETS = {
  /** Агуулах/ondemand-аас татаж хүргэх энгийн захиалга */
  delivery: {
    key: 'delivery',
    label: 'Хүргэлтийн захиалга',
    path: '/order/delivery/add?step=1',
  },
  /** Мерчантаас шууд авч хүргэх (гэрээний төрөл "D") */
  directDelivery: {
    key: 'direct-delivery',
    label: 'Шууд хүргэлт',
    path: '/order/direct-delivery/add?step=1',
  },
};

/**
 * Гэрээний төрлүүдийг харьцуулж болохуйц жагсаалт болгоно.
 *
 * Сервер нь массив (`['WO', 'D']`) эсвэл `'WOD'` мэт үсгүүдийн мөрөөр буцааж
 * болдог (profile/ContractTab-ыг үзнэ үү) тул хоёуланг нь дэмжинэ.
 */
const normalizeContractTypes = (contractType) => {
  if (Array.isArray(contractType)) return contractType.filter(Boolean).map(String);
  if (typeof contractType === 'string' && contractType) return [contractType];
  return [];
};

/**
 * Гэрээний төрлөөс хамаарч "Шинэ захиалга" товч хаашаа орохыг шийднэ.
 *
 *   - Зөвхөн шууд хүргэлтийн (D) гэрээтэй  → шууд хүргэлт үүсгэх хуудас
 *   - Зөвхөн агуулах/ondemand (W, O, WO)   → энгийн хүргэлтийн хуудас
 *   - Хоёулаа                              → товч нь 2 сонголттой цэс болно
 *
 * `'WO'` мэт нийлмэл код бас байдаг тул төрлийг ҮСГЭЭР шалгана.
 * Гэрээ нь тодорхойгүй (татагдаж амжаагүй, танигдахгүй) үед одоогийн зан
 * төлвөөр энгийн хүргэлт рүү оруулна.
 */
export function getAddOrderTargets(contractType) {
  const types = normalizeContractTypes(contractType);

  const targets = [];
  if (types.some((type) => type.includes('W') || type.includes('O'))) {
    targets.push(ADD_ORDER_TARGETS.delivery);
  }
  if (types.some((type) => type.includes('D'))) {
    targets.push(ADD_ORDER_TARGETS.directDelivery);
  }

  return targets.length ? targets : [ADD_ORDER_TARGETS.delivery];
}

/**
 * Хүргэлтийн захиалгын хуудсанд бараа сонгох ЭХ СУРВАЛЖТАЙ гэрээний төрлүүд:
 * агуулахаас (W) эсвэл салбараас (O) татна.
 *
 * Шууд хүргэлт (D) нь агуулахын үлдэгдэлтэй огт харьцдаггүй — мерчант өөрөө
 * бараагаа өгдөг тул тусдаа хуудастай ({@link ADD_ORDER_TARGETS.directDelivery}).
 */
const STOCK_SOURCE_TYPES = ['W', 'O'];

/**
 * Тухайн гэрээний төрөлд бараа сонгох эх сурвалж байгаа эсэх.
 * `'WO'` мэт нийлмэл кодыг ҮСГЭЭР нь таньна.
 */
export function hasStockSource(contractType) {
  return STOCK_SOURCE_TYPES.some((type) => contractType?.includes(type));
}

/**
 * Хүргэлтийн захиалгын 1-р шатанд СОНГУУЛЖ БОЛОХ гэрээний төрлүүд.
 *
 * Мерчантын гэрээнд байгаа боловч энэ хуудсаар үйлчлэх боломжгүй төрлийг
 * (шууд хүргэлт) хасна. Өмнө нь сонголтод үлддэг байсан тул сонгоход бараа
 * сонгох карт огт гарахгүй атлаа "Үргэлжлүүлэх" дарахад "Бараа сонгоогүй
 * байна!" гэж шаардаад хэрэглэгч гацдаг байв.
 */
export function getContractTypeOptions(enumDictionary, contractType) {
  // Гэрээ нь `'WOD'` мөр эсвэл `['WO', 'D']` массив байж болно — үсгээр нь таьна
  const contractHas = (type) =>
    normalizeContractTypes(contractType).some((entry) => entry.includes(type));

  return (enumDictionary?.contractTypes ?? [])
    .filter((type) => contractHas(type) && hasStockSource(type))
    .reduce((options, type) => {
      const label = enumDictionary?.contractDictionary?.[type];
      if (label) options.push({ value: type, label });
      return options;
    }, []);
}

export function getFormattedColumns(unorderedColumns = []) {
  const columnOrders = [
    'code',
    'fromThirdPartyCode',
    'customerPhone',
    'productImages',
    'numberOfNotes',
    'latestNote',
    'branch',
    'orderStatus',
    'isExpress',
    'requestType',
  ];
  return unorderedColumns.slice().sort(function (a, b) {
    return columnOrders.indexOf(a?.key) - columnOrders.indexOf(b?.key);
  });
}
export function getDeliveryTypesDependOnContract(deliveryType, contractType, solvedPrice) {
  // Гэрээ/лавлах мэдээлэл татагдаж амжаагүй байхад цагаан дэлгэц болохгүй
  if (!contractType || !Array.isArray(deliveryType?.type)) return [];

  const includesW = contractType.includes('W');
  const includesWO = contractType.includes('WO');
  const includesO = contractType.includes('O');

  // `reference`-ыг өөрчлөхгүй — шүүсэн жагсаалтаа локал хувилбар болгож авна
  const types = deliveryType.type.filter((type) => type !== 'deliveryHub');

  return types
    ?.filter((item) => {
      if (
        includesW &&
        (solvedPrice === 'B2Cprice' || solvedPrice === 'B2Bprice') &&
        item === 'deliveryselfproclaim'
      ) {
        return true;
      }
      if ((includesW || includesO) && solvedPrice === 'B2Cprice' && item === 'deliverytocabinet')
        return true;
      if (includesWO && solvedPrice === 'B2Bprice' && item === 'delivery') return true;
      if (
        includesWO &&
        solvedPrice === 'B2Cprice' &&
        (item === 'delivery' || item === 'deliverytocabinet')
      )
        return true;
      if (includesWO && solvedPrice === 'B2Cprice' && item === 'deliveryselfproclaim') return true;
      if (item === 'delivery') return true;
      return false;
    })
    ?.map((type) => ({
      type: deliveryType.detail?.[type],
      value: type,
    }));
}
